import * as React from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';

import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserInfo } from '../../user/info';

const drawerWidth: number = 210;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

// TODO remove, this demo shouldn't need to reset the theme.
const defaultTheme = createTheme();

interface MenuItem{
    item:JSX.Element,
    url?:string,
    content?:JSX.Element,
    needPermission?:Array<string>
}
export interface ResponseMenuProps{
    menuPrimary:Array<MenuItem>,
    menuSecondary?:Array<MenuItem>
    headerText:string;
    
    userData:UserInfo
}
export default function ResponsiveMenu({menuPrimary,menuSecondary,headerText,userData}:ResponseMenuProps) {
  const [open, setOpen] = React.useState(false);
  const nav=useNavigate();
  const location = useLocation();
  const mobile = useMediaQuery(defaultTheme.breakpoints.down('md'));
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const component = [...menuPrimary, ...(menuSecondary || [])].find((item) => item.url === location.pathname)?.content;
  function onClickUpdateUrl(url?:string){
      if(!url)
        return;
      if (mobile) setOpen(false);
      nav(url);
  }
  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" open={open && !mobile} elevation={0} sx={{ bgcolor: '#fff', color: '#172b4d', borderBottom: '1px solid #e2e8f0' }}>
          <Toolbar
            sx={{
              pr: '24px', // keep right padding when drawer closed
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Abrir menu" aria-expanded={open}
              onClick={toggleDrawer}
              sx={{
                marginRight: '16px',
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              {headerText}
            </Typography>
            {0?<IconButton color="inherit">
              
            </IconButton>:null}
          </Toolbar>
        </AppBar>
        <Drawer variant={mobile ? "temporary" : "permanent"} open={open} onClose={() => setOpen(false)} sx={mobile ? { '& .MuiDrawer-paper': { position: 'fixed', width: drawerWidth } } : undefined}>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1],
            }}
          >
            {open?<IconButton aria-label="Fechar menu" onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>:null}
          </Toolbar>
          <Divider />
          <List component="nav">
              {menuPrimary.map((item)=>{
                  if(item.needPermission){
                      for(let per of item.needPermission){
                        //@ts-ignore 
                        if(!userData.permission[per]){
                               return null;
                         }
                      }
                  }
                  return (
                    <span key={item.url} onClick={()=>onClickUpdateUrl(item.url)}>
                    {React.cloneElement(item.item, { selected: location.pathname === item.url, 'aria-current': location.pathname === item.url ? 'page' : undefined })}
                    </span>
                  )
              })}
            <Divider sx={{ my: 1 }} />
              {menuSecondary?menuSecondary?.map(item=>{
                  return (<span key={item.url} onClick={()=>onClickUpdateUrl(item.url)}>
                    {React.cloneElement(item.item, { selected: location.pathname === item.url, 'aria-current': location.pathname === item.url ? 'page' : undefined })}
                    </span>)
              }):null}
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: '100dvh',
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          
          <Toolbar />
          <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
            {component}
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}